import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Table, Button, Space, Input, InputNumber, Select, Typography, App,
  Modal, Spin, Popover, Tooltip,
} from 'antd';
import { JobStatusTag } from '@components/StatusTag';
import ApiCodeLink from '@components/ApiCodeLink';
import { ArrowLeftOutlined, SearchOutlined, CopyOutlined, MoreOutlined, LockFilled } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getTaskList,
  getTaskStatusList,
  getSceneGenerationJobs,
  getTaskLogs,
  type TaskListItem,
  type TaskListParams,
  type StatusOption,
  type TaskLogResponse,
} from '@/apis/simulation';
import SceneGenTaskDetailPanel from './SceneGenTaskDetailPanel';

const { Title, Text } = Typography;

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

/**
 * 场景生成 Job 下的 Task 列表：仅查询与列表，不复用任务管理 JobTaskList 页。
 */
export default function SceneGenerationJobTasksPage() {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const jobId = jobIdParam ? Number(jobIdParam) : NaN;

  const [jobName, setJobName] = useState<string>('');
  /** Job 级调度 ID（compute-flow），task 无 taskDispatchId 时展示 */
  const [jobDispatchId, setJobDispatchId] = useState<string>('');
  const [data, setData] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);

  const [page, setPage] = useState({ current: 1, pageSize: 10 });
  const [filterTaskId, setFilterTaskId] = useState<number | undefined>();
  const [filterSceneName, setFilterSceneName] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | number | undefined>();

  const [taskLogModalVisible, setTaskLogModalVisible] = useState(false);
  const [taskLogLoading, setTaskLogLoading] = useState(false);
  const [taskLogData, setTaskLogData] = useState<TaskLogResponse | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);

  const loadStatusList = useCallback(async () => {
    try {
      const list = await getTaskStatusList();
      setStatusList(list);
    } catch {
      message.error('加载状态列表失败');
    }
  }, [message]);

  const buildParams = useCallback(
    (current: number, pageSize: number): TaskListParams => ({
      jobId,
      current,
      pageSize,
      ...(filterTaskId != null && !Number.isNaN(filterTaskId) ? { taskId: filterTaskId } : {}),
      ...(filterSceneName.trim() ? { sceneName: filterSceneName.trim() } : {}),
      ...(filterStatus !== undefined && filterStatus !== '' ? { status: filterStatus } : {}),
    }),
    [jobId, filterTaskId, filterSceneName, filterStatus],
  );

  const loadTaskList = useCallback(
    async (current: number, pageSize: number) => {
      if (!jobId || Number.isNaN(jobId)) {
        message.warning('无效的 Job ID');
        return;
      }
      setLoading(true);
      try {
        const res = await getTaskList(buildParams(current, pageSize));
        setData(res.tasks);
        setTotal(res.page?.totalElements ?? res.total);
        setPage({
          current: res.page?.current ?? current,
          pageSize: res.page?.pageSize ?? pageSize,
        });
      } catch {
        /* axios 拦截器已提示 */
      } finally {
        setLoading(false);
      }
    },
    [jobId, buildParams, message],
  );

  useEffect(() => {
    loadStatusList();
  }, [loadStatusList]);

  useEffect(() => {
    if (!jobId || Number.isNaN(jobId)) return;
    (async () => {
      try {
        const res = await getSceneGenerationJobs({ jobId, current: 1, pageSize: 1 });
        const j = res.tasks?.[0];
        setJobName(j?.jobName || '');
        setJobDispatchId(j?.dispatchJobId || '');
      } catch {
        setJobName('');
        setJobDispatchId('');
      }
    })();
  }, [jobId]);

  useEffect(() => {
    if (!jobId || Number.isNaN(jobId)) return;
    loadTaskList(1, page.pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleSearch = () => {
    loadTaskList(1, page.pageSize);
  };

  const handleReset = () => {
    setFilterTaskId(undefined);
    setFilterSceneName('');
    setFilterStatus(undefined);
    if (!jobId || Number.isNaN(jobId)) return;
    setLoading(true);
    getTaskList({ jobId, current: 1, pageSize: page.pageSize })
      .then((res) => {
        setData(res.tasks);
        setTotal(res.page?.totalElements ?? res.total);
        setPage({
          current: 1,
          pageSize: res.page?.pageSize ?? page.pageSize,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleViewTaskLog = async (taskId: number) => {
    setCurrentTaskId(taskId);
    setTaskLogModalVisible(true);
    setTaskLogData(null);
    setTaskLogLoading(true);
    try {
      const logData = await getTaskLogs(taskId);
      setTaskLogData(logData);
    } catch {
      /* axios 拦截器已提示 */
    } finally {
      setTaskLogLoading(false);
    }
  };

  const handleTaskLogModalCancel = () => {
    setTaskLogModalVisible(false);
    setTaskLogData(null);
    setCurrentTaskId(null);
  };

  const handleCopyReproduceCommand = (sceneName: string) => {
    const cmd = `python3 /home/caros/tools/simulation_tools/scene_run.py --name ${sceneName || ''}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(cmd).then(
        () => message.success('已复制本地仿真命令'),
        () => message.error('复制失败'),
      );
      return;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = cmd;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) message.success('已复制本地仿真命令');
      else message.error('复制失败');
    } catch {
      message.error('复制失败');
    }
  };

  const columns: ColumnsType<TaskListItem> = [
    { title: 'Task ID', dataIndex: 'taskId', width: 100 },
    {
      title: '场景名称',
      dataIndex: 'sceneName',
      width: 200,
      ellipsis: true,
      render: (text: string | null | undefined, record: TaskListItem) => {
        const display = text || '-';
        // 名称点击跳「场景标注」单场景视图,与 FullSceneList 同款交互(target
        // 新页面,不打断当前列表 / 抽屉状态)。onClick stopPropagation:防止冒泡
        // 到 Table 行的展开逻辑。
        const link = record.sceneId ? (
          <Link
            to={`/scene-annotate?sceneId=${record.sceneId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: '#5e6ad2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {display}
          </Link>
        ) : (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
        );
        const isConfidential = (record.securityLevel ?? 0) > 0;
        if (!isConfidential) return link;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {link}
            <Tooltip title="保密：仅具备查看权限的角色可见">
              <LockFilled style={{ color: '#cf1322', fontSize: 12, flexShrink: 0 }} />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      width: 160,
      ellipsis: true,
      // 复用 @components/ApiCodeLink：jira(XX-\d+) → Jira browse；其它 →
      // Case-Flow(按 region 派生 waypulse 域名)；空值 → 灰色占位。
      render: (v: string | null | undefined, record: TaskListItem) => (
        <ApiCodeLink code={v} source={record.source} region={record.region} />
      ),
    },
    { title: '场景 ID', dataIndex: 'sceneId', width: 100 },
    {
      title: '状态', dataIndex: 'statusName', width: 110,
      render: (_: string, record: TaskListItem) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    { title: '任务类型', dataIndex: 'taskTypeName', width: 120 },
    { title: '创建时间', dataIndex: 'createTime', width: 180, render: formatTime },
    { title: '结束时间', dataIndex: 'endTime', width: 180, render: formatTime },
    { title: '创建人', dataIndex: 'creator', width: 100 },
    {
      title: '操作',
      key: 'action',
      width: 88,
      align: 'center',
      fixed: 'right',
      render: (_: unknown, record: TaskListItem) => (
        <Popover
          content={(
            <div style={{ minWidth: 220 }}>
              <div style={{ marginBottom: 8 }}>
                <a
                  onClick={() => handleViewTaskLog(record.taskId)}
                  style={{ color: '#5e6ad2', cursor: 'pointer' }}
                >
                  Task日志
                </a>
              </div>
              <div style={{ marginBottom: 8 }}>
                调度任务ID:
                {' '}
                {record.taskDispatchId || jobDispatchId || '-'}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  本地仿真命令：
                  {`python3 /home/caros/tools/simulation_tools/scene_run.py --name ${record.sceneName || ''}`}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyReproduceCommand(record.sceneName)}
                />
              </div>
            </div>
          )}
          trigger="click"
        >
          <Button size="small" icon={<MoreOutlined />} />
        </Popover>
      ),
    },
  ];

  if (!jobId || Number.isNaN(jobId)) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <Text type="danger">无效的 Job ID</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/scene-generation')}
        >
          返回场景生成
        </Button>
      </Space>
      <Title level={2} style={{ marginBottom: 4 }}>
        场景生成任务 · Task 列表
      </Title>
      <Text type="secondary">
        Job ID: {jobId}
        {jobName ? ` · ${jobName}` : ''}
      </Text>

      <Card style={{ marginTop: 20 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <InputNumber
            placeholder="Task ID"
            value={filterTaskId}
            onChange={(v) => setFilterTaskId(v ?? undefined)}
            onPressEnter={handleSearch}
            style={{ width: 130 }}
            min={1}
          />
          <Input
            placeholder="场景名称"
            value={filterSceneName}
            onChange={(e) => setFilterSceneName(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 160 }}
            value={filterStatus}
            onChange={(v) => setFilterStatus(v)}
            options={statusList.map((s) => ({
              value: s.value,
              label: s.label ?? String(s.value),
            }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>

        <Table<TaskListItem>
          rowKey="taskId"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          expandable={{
            // 行展开抽屉:显示场景备注 / 详细日志 / 豁免按钮。
            // 展开后 lazy 拉 GET /task/{taskId}/scene-info + /task/logs;
            // 豁免成功回调 refetch 当前页,让 status/statusName 列同步更新。
            expandedRowRender: (record) => (
              <SceneGenTaskDetailPanel
                taskId={record.taskId}
                onWaived={() => loadTaskList(page.current, page.pageSize)}
              />
            ),
            // 抽屉展开影响行高较多,收到父层单一 expand 时 rowExpandable 保持默认 true。
          }}
          pagination={{
            current: page.current,
            pageSize: page.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (c, s) => loadTaskList(c, s ?? page.pageSize),
          }}
        />
      </Card>

      <Modal
        title={`Task 日志 - Task ID: ${currentTaskId ?? ''}`}
        open={taskLogModalVisible}
        onCancel={handleTaskLogModalCancel}
        footer={[
          <Button key="close" onClick={handleTaskLogModalCancel}>
            关闭
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        <Spin spinning={taskLogLoading}>
          {taskLogData && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>日志下载链接：</strong>
                  {taskLogData.logDownloadUrl ? (
                    <a
                      href={taskLogData.logDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#5e6ad2', marginLeft: 8 }}
                    >
                      {taskLogData.logDownloadUrl}
                    </a>
                  ) : (
                    <span style={{ marginLeft: 8, color: '#9b9da3' }}>无</span>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>S3路径：</strong>
                  <span style={{ marginLeft: 8 }}>{taskLogData.logS3Path || '-'}</span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>总行数：</strong>
                  <span style={{ marginLeft: 8 }}>{taskLogData.totalLines || 0}</span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>显示行数：</strong>
                  <span style={{ marginLeft: 8 }}>
                    {taskLogData.tailLines || 0}
                    （最后
                    {' '}
                    {taskLogData.tailLines || 0}
                    {' '}
                    行）
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <strong>日志内容：</strong>
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    backgroundColor: '#18181b',
                    color: '#a1a1aa',
                    borderRadius: 6,
                    border: '1px solid #27272a',
                    maxHeight: 500,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'JetBrains Mono', Consolas, monospace",
                    fontSize: 12,
                  }}
                >
                  {taskLogData.logContent || '无日志内容'}
                </div>
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
}
